import {
  lazy,
  Suspense,
  useMemo,
  type ComponentProps,
  type ComponentType,
} from "react";
import { iconMap, type LazyIconName } from "./iconMap";
import { IconWrapper } from "./IconWrapper";
import type { FeatherAArrowDown } from "@subframe/core";

// @ts-ignore Vite glob imports are resolved at build time
const icons = import.meta.glob(
  "../node_modules/@subframe/core/dist/esm/assets/icons/final/*.js",
  {
    eager: false,
  }
) as Record<
  string,
  () => Promise<{
    default: ComponentType<ComponentProps<typeof FeatherAArrowDown>>;
  }>
>;

export interface LazyIconProps {
  icon: keyof typeof iconMap | null;
}

const EmptyIcon = () => {
  return (
    <IconWrapper>
      <svg width="1em" height="1em"></svg>
    </IconWrapper>
  );
};

const iconCache = new Map<
  LazyIconName,
  ComponentType<ComponentProps<typeof FeatherAArrowDown>>
>();

function getIconComponent(iconName: LazyIconName | null) {
  if (iconName === null) return null;
  if (!iconCache.has(iconName)) {
    const importEntry =
      icons[
        `../node_modules/@subframe/core/dist/esm/assets/icons/final/feather-${iconMap[iconName]}.js`
      ];
    if (!importEntry) {
      if (import.meta.env.DEV) {
        console.warn(`Icon not found for name: ${iconName}`);
      }
      return null;
    }

    const lazyIcon = lazy(() => importEntry());
    iconCache.set(iconName, lazyIcon);
  }
  return iconCache.get(iconName)!;
}

export const LazyIcon = ({ icon }: LazyIconProps) => {
  const IconComp = useMemo(() => getIconComponent(icon), [icon]);
  if (!IconComp || icon === "empty") return <EmptyIcon />;
  return (
    <Suspense fallback={<EmptyIcon />}>
      <IconComp />
    </Suspense>
  );
};
